import { RefObject, useCallback, useEffect, useRef, useState } from 'react';

const OPTIMISTIC_ID_PREFIX = 'optimistic-';
const SCROLL_BOTTOM_THRESHOLD_PX = 16;
const PAGINATION_SEAM_OFFSET_PX = 80;

const usePrevious = <T>(value: T): T | undefined => {
  const ref = useRef<T | undefined>(undefined);
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

type Params = {
  scrollRef: RefObject<HTMLDivElement>;
  isInitialLoading: boolean;
  lastReplyId: string | undefined;
  pagesCount: number;
  repliesCount: number;
};

export const useCommentsScrollBehavior = ({
  scrollRef,
  isInitialLoading,
  lastReplyId,
  pagesCount,
  repliesCount,
}: Params) => {
  const [hasNewComment, setHasNewComment] = useState(false);
  const previousLastReplyId = usePrevious(lastReplyId);
  const previousPagesCount = usePrevious(pagesCount) ?? 0;
  const previousRepliesCount = usePrevious(repliesCount) ?? 0;
  const pendingOwnScrollRef = useRef(false);
  const hasInitialScrolledRef = useRef(false);
  const preFetchScrollRef = useRef<{ height: number; top: number } | null>(
    null,
  );

  const scrollToBottom = useCallback(
    (behavior: ScrollBehavior) => {
      scrollRef.current?.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior,
      });
    },
    [scrollRef],
  );

  useEffect(() => {
    if (isInitialLoading) return;
    if (lastReplyId === previousLastReplyId) return;

    const isOptimisticReplacement =
      previousLastReplyId?.startsWith(OPTIMISTIC_ID_PREFIX) &&
      lastReplyId !== undefined &&
      !lastReplyId.startsWith(OPTIMISTIC_ID_PREFIX);
    if (isOptimisticReplacement) return;

    if (!hasInitialScrolledRef.current) {
      hasInitialScrolledRef.current = true;
      scrollToBottom('instant');
      return;
    }

    if (pendingOwnScrollRef.current) {
      pendingOwnScrollRef.current = false;
      scrollToBottom('smooth');
      return;
    }

    if (repliesCount > previousRepliesCount) {
      setHasNewComment(true);
    }
  }, [
    isInitialLoading,
    lastReplyId,
    previousLastReplyId,
    repliesCount,
    previousRepliesCount,
    scrollToBottom,
  ]);

  useEffect(() => {
    if (
      pagesCount > previousPagesCount &&
      preFetchScrollRef.current &&
      scrollRef.current
    ) {
      const newHeight = scrollRef.current.scrollHeight;
      const heightDelta = newHeight - preFetchScrollRef.current.height;
      const preservedTop = preFetchScrollRef.current.top + heightDelta;
      scrollRef.current.scrollTop = preservedTop;
      scrollRef.current.scrollTo({
        top: preservedTop - PAGINATION_SEAM_OFFSET_PX,
        behavior: 'smooth',
      });
      preFetchScrollRef.current = null;
    }
  }, [pagesCount, previousPagesCount, scrollRef]);

  const markOwnSubmit = useCallback(() => {
    pendingOwnScrollRef.current = true;
  }, []);

  const captureScrollBeforePagination = useCallback(() => {
    if (scrollRef.current) {
      preFetchScrollRef.current = {
        height: scrollRef.current.scrollHeight,
        top: scrollRef.current.scrollTop,
      };
    }
  }, [scrollRef]);

  const scrollToNewComment = useCallback(() => {
    setHasNewComment(false);
    scrollToBottom('smooth');
  }, [scrollToBottom]);

  const handleScroll = useCallback(() => {
    if (!hasNewComment || !scrollRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    if (distanceFromBottom <= SCROLL_BOTTOM_THRESHOLD_PX) {
      setHasNewComment(false);
    }
  }, [hasNewComment, scrollRef]);

  return {
    hasNewComment,
    markOwnSubmit,
    captureScrollBeforePagination,
    scrollToNewComment,
    handleScroll,
  };
};
